<script setup>
import { useCommonCodeStore } from '@/store/commoncode-store';
import { computed, onMounted, reactive, ref, watch } from 'vue';

const props = defineProps({
    contractType: {
        type: String,
        default: 'INSU'
    },
    validMsg: {
        type: Object,
        default: () => ({})
    },
    requiredList: {
        type: [Array, Object],
        default: () => []
    },
    readonlyList: {
        type: [Array, Object],
        default: () => []
    },
    hideList: {
        type: [Array, Object],
        default: () => []
    },
    accidentData: {
        type: Object,
        default: () => ({})
    }
});

const emit = defineEmits(['update']);
const commonCodes = computed(() => useCommonCodeStore().getCommonCodes);
const commonCode = (code) => {
    const returnVal = computed(() => {
        return commonCodes.value[code];
    });
    return returnVal;
};

const initialData = {
    accident_vehicle_no: '', // 사고차량번호
    accident_vehicle_model: '', // 사고차량모델
    accident_vehicle_year: '', // 사고차량제조년
    accident_vehicle_displacement: '', // 사고차량배기량
    insurance_company: '', // 보험사
    insurance_no: '', // 보험접수번호
    insurance_manager: '',
    insurance_manager_contact: '',
    insurance_manager_fax: '',
    negligence_ratio: '',
    insurance_notice: '',
    repair_shop: '', // 공업사
    repair_shop_contact: '' // 공업사연락처
};

const data = reactive({ ...initialData });

const requiredData = [];

const requiredKeys = computed(() => {
    const res = props.requiredList;
    if (Array.isArray(res) && res.length) return res;
    if (res && typeof res === 'object' && Object.keys(res).length) {
        return Object.keys(res).filter((key) => !!res[key]);
    }
    return requiredData;
});

const requiredKeySet = computed(() => new Set(requiredKeys.value));
const isRequired = (key) => requiredKeySet.value.has(key);

const initialValidMessage = {
    accident_vehicle_no: '',
    accident_vehicle_model: '',
    accident_vehicle_year: '',
    accident_vehicle_displacement: '',
    insurance_company: '',
    insurance_no: '',
    insurance_manager: '',
    insurance_manager_contact: '',
    insurance_manager_fax: '',
    repair_shop: '',
    repair_shop_contact: ''
};
const validationMessage = reactive({ ...initialValidMessage });

const propsReadonlyEnforced = computed(() => {
    const res = props.readonlyList;
    if (Array.isArray(res)) return new Set(res);
    if (res && typeof res === 'object') return new Set(Object.keys(res).filter((k) => !!res[k]));
    return new Set();
});

const propsReadonlyControlled = computed(() => {
    const res = props.readonlyList;
    if (Array.isArray(res)) return new Set(res);
    if (res && typeof res === 'object') return new Set(Object.keys(res));
    return new Set();
});

const allReadonly = ref(false);
const setAllReadonly = (val = true) => {
    allReadonly.value = !!val;
};
const allowedKeys = Object.keys(initialData);

const internalReadonlyKeys = computed(() => {
    const set = new Set();
    if (allReadonly.value) allowedKeys.forEach((k) => set.add(k));
    return set;
});

const finalReadonlySet = computed(() => {
    const result = new Set(propsReadonlyEnforced.value);
    internalReadonlyKeys.value.forEach((k) => {
        if (!propsReadonlyControlled.value.has(k)) result.add(k);
    });
    return result;
});

const isFieldReadonly = (key) => finalReadonlySet.value.has(key);
const isEffectivelyRequired = (key) => requiredKeySet.value.has(key) && !isFieldReadonly(key) && !isHidden(key);

const hideKeySet = computed(() => {
    const res = props.hideList;
    if (Array.isArray(res) && res.length) return new Set(res);
    if (res && typeof res === 'object' && Object.keys(res).length) {
        return new Set(Object.keys(res).filter((key) => !!res[key]));
    }
    return new Set();
});
const isHidden = (key) => hideKeySet.value.has(key);

const resetData = () => {
    Object.assign(data, initialData);
    Object.assign(validationMessage, initialValidMessage);
};

const getData = () => {
    const payload = {};
    for (const key of allowedKeys) {
        if (!isHidden(key)) payload[key] = data[key];
    }
    return payload;
};

const setAccidentData = (newVal) => {
    if (!newVal) return;
    const payload = newVal.contract_data ?? newVal;

    Object.entries(payload).forEach(([key, val]) => {
        if (allowedKeys.includes(key)) {
            data[key] = val;
        }
    });
};

const checkRequired = () => {
    Object.keys(validationMessage).forEach((k) => (validationMessage[k] = ''));
    let result = true;

    const keysToCheck = new Set(requiredKeys.value.filter((key) => allowedKeys.includes(key) && isEffectivelyRequired(key)));

    for (const key of keysToCheck) {
        const val = data[key];
        const isEmpty = val === '' || val === null || val === undefined;
        const isIdInvalid = key.endsWith('_id') && (isEmpty || Number(val) === 0);
        if (isEmpty || isIdInvalid) {
            if (key in validationMessage) validationMessage[key] = '필수입력입니다.';
            result = false;
        }
    }
    return result;
};

const setErrorMessage = (payload, reset = false) => {
    if (!payload) return;

    if (reset) {
        Object.keys(validationMessage).forEach((k) => (validationMessage[k] = ''));
    }

    const collectPairs = (node, out = []) => {
        if (node == null) return out;

        if (Array.isArray(node)) {
            node.forEach((item) => collectPairs(item, out));
            return out;
        }

        if (typeof node === 'object') {
            for (const [k, v] of Object.entries(node)) {
                if (v && typeof v === 'object' && !Array.isArray(v)) {
                    collectPairs(v, out);
                } else {
                    out.push([k, v]);
                }
            }
            return out;
        }

        return out;
    };

    const pairs = collectPairs(payload);

    for (const [rawKey, rawVal] of pairs) {
        let key = String(rawKey).replace(/^contract_data\./, '');
        if (!(key in validationMessage)) continue;

        let msg = rawVal;
        if (Array.isArray(msg)) msg = msg.filter(Boolean).join(', ');
        if (msg == null) msg = '';

        validationMessage[key] = String(msg);
    }
};

function handleInsuranceManagerCompleted(inputId, { value, company, manager_contact, manager_fax }) {
    if (value === '') return;
    data[`${inputId}_company`] = company;
    data[`${inputId}_manager_contact`] = manager_contact;
    data[`${inputId}_manager_fax`] = manager_fax;
}

onMounted(() => {
    //resetData();
    // if (props.accidentData && Object.keys(props.accidentData).length > 0) {
    //     setAccidentData(props.accidentData);
    // }
});

watch(
    () => props.accidentData,
    (newVal) => {
        if (!newVal || typeof newVal !== 'object') return;
        setAccidentData(newVal);
    },
    { immediate: true, deep: false }
);

watch(
    () => props.readonlyList,
    () => {
        Object.keys(validationMessage).forEach((key) => {
            if (isFieldReadonly(key)) validationMessage[key] = '';
        });
        // checkRequired();
    },
    { immediate: false, deep: false }
);

watch(
    () => props.hideList,
    () => {
        Object.keys(validationMessage).forEach((key) => {
            if (isHidden(key)) validationMessage[key] = '';
        });
    },
    { immediate: false, deep: false }
);

watch(
    () => props.validMsg,
    (newVal) => {
        if (!newVal || typeof newVal !== 'object') {
            console.error('Invalid Error data');
            return;
        }
        setErrorMessage(newVal, true);
        // Object.keys(newVal).forEach((key) => {
        //     if (key.startsWith('contract_data.')) {
        //         const pureKey = key.replace('contract_data.', '');
        //         if (pureKey in validationMessage) {
        //             validationMessage[pureKey] = newVal[key];
        //         }
        //     } else {
        //         if (key in validationMessage) {
        //             validationMessage[key] = newVal[key];
        //         }
        //     }
        // });
    },
    { immediate: true }
);

defineExpose({
    getData,
    resetData,
    setAccidentData,
    checkRequired,
    setAllReadonly,
    setErrorMessage
});
</script>

<template>
    <div class="section-box base-text">
        <h2 class="section-title py-4">사고차량 정보</h2>
        <div class="section-body">
            <div class="section-left">
                <div class="section-row">
                    <div class="input-box w-[50%]">
                        <inputbox
                            inputId="accident_vehicle_no"
                            v-model="data.accident_vehicle_no"
                            :errmsg="validationMessage.accident_vehicle_no"
                            :readonly="isFieldReadonly('accident_vehicle_no')"
                            :required="isEffectivelyRequired('accident_vehicle_no')"
                            label="사고 차량번호"
                            :placeholder="'사고 차량번호를 입력하세요.'"
                        />
                    </div>
                    <div class="input-box w-[50%]">
                        <inputbox
                            inputId="accident_vehicle_model"
                            v-model="data.accident_vehicle_model"
                            :errmsg="validationMessage.accident_vehicle_model"
                            :readonly="isFieldReadonly('accident_vehicle_model')"
                            :required="isEffectivelyRequired('accident_vehicle_model')"
                            label="사고 차종"
                            :placeholder="'사고 차종을 입력하세요.'"
                        />
                    </div>
                </div>
                <div class="section-row items-center">
                    <div class="input-box w-[50%]">
                        <dateyear
                            input="accident_vehicle_year"
                            :readonly="isFieldReadonly('accident_vehicle_year')"
                            :required="isEffectivelyRequired('accident_vehicle_year')"
                            v-model="data.accident_vehicle_year"
                            :errmsg="validationMessage.accident_vehicle_year"
                            label="사고 차량연식"
                        />
                    </div>
                    <div class="input-box w-[50%]">
                        <inputbox
                            inputId="accident_vehicle_displacement"
                            :number="'n'"
                            :placeholder="'사고차량 배기량을 입력하세요.'"
                            :unit="'CC'"
                            v-model="data.accident_vehicle_displacement"
                            :errmsg="validationMessage.accident_vehicle_displacement"
                            label="배기량"
                            :maxlength="6"
                            :readonly="isFieldReadonly('accident_vehicle_displacement')"
                            :required="isEffectivelyRequired('accident_vehicle_displacement')"
                        />
                    </div>
                </div>
                <div v-if="!isHidden('repair_shop') || !isHidden('repair_shop_contact')" class="section-row">
                    <div class="input-box w-[50%]">
                        <inputbox
                            inputId="repair_shop"
                            v-model="data.repair_shop"
                            :errmsg="validationMessage.repair_shop"
                            label="공업사"
                            :placeholder="'공업사를 입력하세요.'"
                            :readonly="isFieldReadonly('repair_shop')"
                            :required="isEffectivelyRequired('repair_shop')"
                        />
                    </div>
                    <div class="input-box w-[50%]">
                        <inputbox
                            inputId="repair_shop_contact"
                            v-model="data.repair_shop_contact"
                            :errmsg="validationMessage.repair_shop_contact"
                            label="공업사 연락처"
                            :placeholder="'공업사 연락처를 입력하세요.'"
                            :mask="'phone'"
                            :readonly="isFieldReadonly('repair_shop_contact')"
                            :required="isEffectivelyRequired('repair_shop_contact')"
                        />
                    </div>
                </div>
            </div>
            <div class="section-right">
                <div class="section-row">
                    <div class="input-box w-[50%]">
                        <combobox
                            inputId="insurance_company"
                            :options="commonCode('INSURANCE_COMPANY').value"
                            v-model="data.insurance_company"
                            :errmsg="validationMessage.insurance_company"
                            label="보험사"
                            :readonly="isFieldReadonly('insurance_company')"
                            :required="isEffectivelyRequired('insurance_company')"
                        />
                    </div>
                    <div class="input-box w-[50%]">
                        <inputbox
                            inputId="insurance_no"
                            v-model="data.insurance_no"
                            :errmsg="validationMessage.insurance_no"
                            label="접수번호"
                            :placeholder="'접수번호를 입력하세요.'"
                            :readonly="isFieldReadonly('insurance_no')"
                            :required="isEffectivelyRequired('insurance_no')"
                        />
                    </div>
                </div>

                <div v-if="!isHidden('insurance_manager') || !isHidden('insurance_manager_contact')" class="section-row">
                    <div class="input-box w-[50%]">
                        <insurance
                            inputId="insurance_manager"
                            :placeholder="'보험사 담당자를 입력하세요.'"
                            v-model="data.insurance_manager"
                            :errmsg="validationMessage.insurance_manager"
                            :readonly="isFieldReadonly('insurance_manager')"
                            :required="isEffectivelyRequired('insurance_manager')"
                            @input="(payload) => handleInsuranceManagerCompleted('insurance', payload)"
                            label="보험사 담당자"
                        />
                    </div>
                    <div class="input-box w-[50%]">
                        <inputbox
                            inputId="insurance_manager_contact"
                            v-model="data.insurance_manager_contact"
                            :errmsg="validationMessage.insurance_manager_contact"
                            label="보험사 담당자 연락처"
                            :placeholder="'보험사 담당자 연락처를 입력하세요.'"
                            :readonly="isFieldReadonly('insurance_manager_contact')"
                            :required="isEffectivelyRequired('insurance_manager_contact')"
                        />
                    </div>
                </div>
                <div v-if="!isHidden('insurance_manager_fax') || !isHidden('negligence_ratio')" class="section-row">
                    <div class="input-box w-[50%]">
                        <inputbox
                            inputId="negligence_ratio"
                            v-model="data.negligence_ratio"
                            :number="'n'"
                            :unit="'%'"
                            :errmsg="validationMessage.negligence_ratio"
                            label="과실비율"
                            :placeholder="'과실비율을 입력하세요.'"
                            :maxlength="3"
                            :readonly="isFieldReadonly('negligence_ratio')"
                            :required="isEffectivelyRequired('negligence_ratio')"
                        />
                    </div>
                    <div class="input-box w-[50%]">
                        <inputbox
                            inputId="insurance_manager_faxt"
                            v-model="data.insurance_manager_fax"
                            :errmsg="validationMessage.insurance_manager_fax"
                            label="보험사 담당자 팩스"
                            :placeholder="'보험사 담당자 팩스를 입력하세요.'"
                            :readonly="isFieldReadonly('insurance_manager_fax')"
                            :required="isEffectivelyRequired('insurance_manager_fax')"
                        />
                    </div>
                </div>
                <div v-if="!isHidden('insurance_notice')" class="section-row">
                    <div class="input-box w-[100%]">
                        <customtextbox
                            inputId="insurance_notice"
                            v-model="data.insurance_notice"
                            :height="'5rem'"
                            :errmsg="validationMessage.insurance_notice"
                            label="보험사 전달사항"
                            :placeholder="'보험사 전달사항을 입력하세요'"
                            :readonly="isFieldReadonly('insurance_notice')"
                            :required="isEffectivelyRequired('insurance_notice')"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<style src="@/assets/css/form-common.css"></style>
